import {
    marketGroupLabel,
    buildRunReportPath,
    buildScoreboard,
    buildScoreboardMarkdown,
    describePromotionBlocker,
    describeReportSlice,
    parseReportViewParams,
    resolveReportView,
    writeReportViewParams,
    type ReportView
} from './reportView';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, ConfirmationDialog, DataTable, Disclosure, StatusBadge, Tooltip } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useOptionBuyingStrategies } from '@/hooks/useOptionBuyingStrategies';
import { formatOptionBuyingStrategyName } from '@utils/optionBuyingStrategyHelpers';
import CopySummaryButton from './components/CopySummaryButton';
import CreateRunModal from './components/CreateRunModal';
import PromoteVariantModal from './components/PromoteVariantModal';
import PromotionReceiptCard from './components/PromotionReceiptCard';
import RunDiagnostics from './components/RunDiagnostics';
import RunCoverageSummary from './components/RunCoverageSummary';
import { describeEntryCoverage } from './coverageView';
import RunExposureCallout from './components/RunExposureCallout';
import RunReportMatrix from './components/RunReportMatrix';
import {
    computeRunProgress,
    describeRunExposure,
    formatHorizonLabel,
    formatSignedPercent,
    getRunStatusTone,
    isIdeaLabRunActive,
    parsePositiveInt,
    RUN_STATUS_LABELS,
    type IdeaLabRunDetail,
    type IdeaLabRunVariant
} from './ideaLabUtils';
import { describeRunFacts } from './studyView';

type FailedWorkUnit = IdeaLabRunDetail['failedWorkUnits'][number];

const WORK_UNIT_PHASE_LABELS: Record<IdeaLabRunDetail['workUnits'][number]['phase'], string> = {
    evaluate: 'Evaluate',
    reduce: 'Reduce'
};

/**
 * One run, results first: a one-line summary of what was frozen, the report (scoreboard, then the
 * selected variant's detail), and everything about execution folded into Diagnostics. While the
 * run is in flight the page shows its progress instead. The report slice lives in the URL so any
 * view can be linked.
 */
const IdeaLabRun = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const { runId: runIdParam } = useParams();
    const runId = parsePositiveInt(runIdParam ?? null);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [promoteTarget, setPromoteTarget] = useState<IdeaLabRunVariant | null>(null);
    const [isRerunOpen, setIsRerunOpen] = useState(false);
    const [comparisonRunIds, setComparisonRunIds] = useState<number[]>([]);

    useEffect(() => setComparisonRunIds([]), [runId]);

    const runQuery = useAppQuery(
        {
            ...trpc.ideaLab.getRun.queryOptions({ runId: runId ?? 0 }),
            enabled: runId !== null,
            refetchInterval: (query) => {
                const current = query.state.data;

                return current && isIdeaLabRunActive(current) ? 5000 : false;
            }
        },
        { fallbackErrorMessage: 'Failed to load the run' }
    );
    const run = runQuery.data ?? null;
    const studyQuery = useAppQuery(
        {
            ...trpc.ideaLab.getStudy.queryOptions({ studyId: run?.studyId ?? 0 }),
            enabled: run !== null
        },
        { fallbackErrorMessage: 'Failed to load the study' }
    );
    const study = studyQuery.data ?? null;
    const { strategiesById: optionStrategiesById } = useOptionBuyingStrategies();
    const optionStrategy = run?.optionLens ? (optionStrategiesById.get(run.optionLens.optionBuyingStrategyId) ?? null) : null;
    const optionStrategyName = optionStrategy ? formatOptionBuyingStrategyName(optionStrategy) : null;
    const reportQuery = useAppQuery(
        {
            ...trpc.ideaLab.getRunReport.queryOptions({ runId: runId ?? 0 }),
            enabled: runId !== null && run?.status === 'completed',
            staleTime: Infinity
        },
        { fallbackErrorMessage: 'Failed to load the run report' }
    );
    const report = reportQuery.data ?? null;
    const comparisonCandidatesQuery = useAppQuery(
        {
            ...trpc.ideaLab.listRuns.queryOptions({ studyId: run?.studyId ?? 0, limit: 50 }),
            enabled: run !== null && run.status === 'completed'
        },
        { fallbackErrorMessage: 'Failed to load comparison runs' }
    );
    const selectedComparisonIds = runId === null ? [] : [runId, ...comparisonRunIds];
    const comparisonQuery = useAppQuery(
        {
            ...trpc.ideaLab.compareRuns.queryOptions({
                runIds: selectedComparisonIds
            }),
            enabled: selectedComparisonIds.length >= 2
        },
        { fallbackErrorMessage: 'Failed to compare runs' }
    );
    const comparisonCandidates = useMemo(
        () => (comparisonCandidatesQuery.data ?? []).filter((candidate) => candidate.id !== runId && candidate.status === 'completed'),
        [comparisonCandidatesQuery.data, runId]
    );
    const toggleComparisonRun = (candidateId: number) =>
        setComparisonRunIds((current) => {
            if (current.includes(candidateId)) return current.filter((id) => id !== candidateId);
            if (current.length >= 4) return current;

            return [...current, candidateId];
        });

    const promotionsQuery = useAppQuery(
        {
            ...trpc.ideaLab.listPromotions.queryOptions({ studyId: run?.studyId ?? 0, limit: 200 }),
            enabled: run !== null
        },
        { fallbackErrorMessage: 'Failed to load promotions' }
    );
    const runPromotions = useMemo(
        () => (promotionsQuery.data ?? []).filter((promotion) => promotion.runId === runId),
        [promotionsQuery.data, runId]
    );

    const cancelMutation = useMutation({
        ...trpc.ideaLab.cancelRun.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (cancelled) => {
            showToast(`Cancelled run "${cancelled.label}"`, 'success');
            setConfirmCancel(false);
            await Promise.all([
                queryClient.invalidateQueries(trpc.ideaLab.getRun.pathFilter()),
                queryClient.invalidateQueries(trpc.ideaLab.listRuns.pathFilter())
            ]);
        }
    });
    const retryMutation = useMutation({
        ...trpc.ideaLab.retryRun.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (retried) => {
            showToast(`Queued exact retry "${retried.label}"`, 'success');
            await queryClient.invalidateQueries(trpc.ideaLab.listRuns.pathFilter());
            void navigate(`/user/ideaLab/run/${retried.id}`);
        }
    });

    const view = useMemo(() => (report ? resolveReportView(report, parseReportViewParams(searchParams)) : null), [report, searchParams]);
    const updateView = useCallback(
        (patch: Partial<ReportView>) => {
            if (!report || !view) return;

            setSearchParams(writeReportViewParams(searchParams, resolveReportView(report, { ...view, ...patch })), { replace: true });
        },
        [report, searchParams, setSearchParams, view]
    );

    const progress = useMemo(() => (run ? computeRunProgress(run) : null), [run]);
    const variantsById = useMemo(() => new Map((run?.variants ?? []).map((variant) => [variant.variantId, variant])), [run]);
    const promotionBlocker = run ? describePromotionBlocker(run) : null;
    const canRunAgain = study?.status === 'open';

    const failedColumns: ColumnDef<FailedWorkUnit>[] = [
        {
            id: 'entity',
            header: 'Symbol',
            cell: ({ row }) => {
                if (row.original.entityId === null) return <span className="text-zinc-600">—</span>;

                const entity = run?.entities.find(
                    (candidate) => candidate.entityType === row.original.entityType && candidate.entityId === row.original.entityId
                );

                return `${entity?.symbol ?? row.original.entityId} (${row.original.entityType})`;
            }
        },
        { accessorKey: 'phase', header: 'Phase', cell: ({ row }) => WORK_UNIT_PHASE_LABELS[row.original.phase] },
        { accessorKey: 'retryCount', header: 'Retries' },
        { accessorKey: 'transientRetryCount', header: 'Transient' },
        {
            accessorKey: 'errorMessage',
            header: 'Error',
            cell: ({ row }) => <span className="break-words text-rose-300">{row.original.errorMessage ?? '—'}</span>
        },
        { accessorKey: 'id', header: 'Unit' }
    ];

    const renderPromoteAction = (variant: IdeaLabRunVariant) =>
        promotionBlocker === null ? (
            <Button size="sm" variant="primary" onClick={() => setPromoteTarget(variant)}>
                Promote to strategy
            </Button>
        ) : (
            <Tooltip content={promotionBlocker}>
                <Button className="pointer-events-none" disabled size="sm" variant="outline-dark">
                    Promote to strategy
                </Button>
            </Tooltip>
        );

    if (runId === null) {
        return (
            <UserLayout containerSize="1400px" title="Idea Lab Run - Project 314">
                <Callout variant="error">Invalid run id.</Callout>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1400px" title={`${run?.label ?? `Run #${runId}`} - Idea Lab - Project 314`}>
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    {run && (
                        <Link className="text-xs text-zinc-500 hover:text-zinc-300" to={`/user/ideaLab/study/${run.studyId}`}>
                            ← {study?.name ?? `Study #${run.studyId}`}
                        </Link>
                    )}
                    <h1 className="text-2xl font-semibold text-zinc-100">
                        {run?.label ?? `Run #${runId}`}
                        <span className="ml-2 align-middle text-sm font-normal text-zinc-500">#{runId}</span>
                    </h1>
                    {run && (
                        <p className="mt-1 flex flex-wrap gap-x-2 text-sm text-zinc-400">
                            {describeRunFacts(run, optionStrategyName).map((fact, index) => (
                                <span key={fact}>
                                    {index > 0 && (
                                        <span aria-hidden="true" className="mr-2 text-zinc-600">
                                            ·
                                        </span>
                                    )}
                                    {fact}
                                </span>
                            ))}
                        </p>
                    )}
                </div>
                {run && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <StatusBadge pulse={isIdeaLabRunActive(run)} status={run.status} tone={getRunStatusTone(run.status)}>
                            {RUN_STATUS_LABELS[run.status]}
                        </StatusBadge>
                        {run.status === 'completed' && (
                            <Button size="sm" to={`/user/ideaLab/run/${run.id}/review`} variant="outline-dark">
                                Visual Validator
                            </Button>
                        )}
                        {isIdeaLabRunActive(run) && (
                            <Button size="sm" variant="outline-dark" onClick={() => setConfirmCancel(true)}>
                                Cancel run
                            </Button>
                        )}
                        {canRunAgain && !isIdeaLabRunActive(run) && (
                            <Button
                                size="sm"
                                title="Open the New run form with every setting of this run filled in"
                                variant={run.status === 'completed' ? 'primary' : 'secondary'}
                                onClick={() => setIsRerunOpen(true)}
                            >
                                Run again with changes
                            </Button>
                        )}
                        {(run.status === 'failed' || run.status === 'cancelled') && (
                            <Button
                                isLoading={retryMutation.isPending}
                                size="sm"
                                variant="primary"
                                onClick={() => retryMutation.mutate({ runId: run.id })}
                            >
                                Retry exact run
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {runQuery.isLoading ? (
                <DataLoadingSpinner text="Loading run..." />
            ) : runQuery.error instanceof Error ? (
                <Callout variant="error">{runQuery.error.message}</Callout>
            ) : run && progress ? (
                <div className="space-y-6">
                    {run.failureMessage && (
                        <Callout title="Run failed" variant="error">
                            {run.failureMessage}
                        </Callout>
                    )}

                    {isIdeaLabRunActive(run) && (
                        <Card title="Progress">
                            <div className="flex items-baseline gap-3">
                                <div className="text-3xl font-semibold text-zinc-100">{progress.percent}%</div>
                                <div className="text-sm text-zinc-400">
                                    {progress.completed} of {progress.total} work units done
                                    {progress.failed > 0 ? ` · ${progress.failed} failed` : ''}
                                    {progress.excluded > 0 ? ` · ${progress.excluded} excluded` : ''}
                                </div>
                            </div>
                            <div className="mt-3 h-2 overflow-hidden rounded bg-zinc-800">
                                <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress.percent}%` }} />
                            </div>
                            <p className="mt-3 text-xs text-zinc-500">
                                Workers evaluate one symbol per work unit, then a reduce unit builds the report. This page refreshes on its
                                own; you do not need to keep it open.
                            </p>
                        </Card>
                    )}

                    {run.failedWorkUnits.length > 0 && (
                        <Card
                            subtitle="Unknown failures and failures beyond the bounded coverage allowance stop the run; pending units are cancelled and no report is produced."
                            title={`Failed work units (${run.failedWorkUnits.length})`}
                        >
                            <DataTable columns={failedColumns} data={run.failedWorkUnits} search={false} />
                        </Card>
                    )}

                    {run.optionLens !== null && run.optionLens.fixedOptionType === null && (
                        <Callout title="Option results are reference only" variant="warning">
                            This run did not record a call or put choice for its option lens, so its option results cannot be promoted
                            {run.includeUnderlying ? ' (the underlying lens still can)' : ''}. Use Run again with changes and choose Call or
                            Put to get promotable option results.
                        </Callout>
                    )}

                    {run.status === 'completed' && (
                        <Card
                            headerActions={
                                report && view ? (
                                    <CopySummaryButton
                                        buildText={() =>
                                            buildScoreboardMarkdown({
                                                title: `Idea Lab run #${run.id}: ${run.label}`,
                                                facts: [
                                                    ...describeRunFacts(run, optionStrategyName),
                                                    describeEntryCoverage(report.entryCoverage)
                                                ],
                                                sliceLabel: describeReportSlice(view),
                                                scoreboard: buildScoreboard(report, view),
                                                url: `${window.location.origin}${buildRunReportPath(run.id, view)}`,
                                                exposureNote: describeRunExposure(report.exposure, run).body
                                            })
                                        }
                                    />
                                ) : undefined
                            }
                            title="Results"
                        >
                            {reportQuery.isLoading ? (
                                <DataLoadingSpinner text="Loading report..." />
                            ) : reportQuery.error instanceof Error ? (
                                <Callout variant="error">{reportQuery.error.message}</Callout>
                            ) : report && view ? (
                                <div className="space-y-4">
                                    <RunCoverageSummary coverage={report.entryCoverage} run={run} />
                                    <RunExposureCallout exposure={report.exposure} run={run} />
                                    <RunReportMatrix
                                        entities={run.entities}
                                        renderVariantActions={renderPromoteAction}
                                        report={report}
                                        view={view}
                                        onViewChange={updateView}
                                    />
                                </div>
                            ) : null}
                        </Card>
                    )}

                    {runPromotions.length > 0 && (
                        <Card
                            subtitle="Each receipt is an inactive strategy created from this run's frozen logic, with its post-commit entry verification."
                            title={`Promotions (${runPromotions.length})`}
                        >
                            <div className="space-y-3">
                                {runPromotions.map((promotion) => (
                                    <PromotionReceiptCard
                                        key={promotion.id}
                                        promotion={promotion}
                                        variantLabel={variantsById.get(promotion.variantId)?.label}
                                    />
                                ))}
                            </div>
                        </Card>
                    )}

                    {run.status === 'completed' && comparisonCandidates.length > 0 && (
                        <Disclosure
                            badge={
                                <span className="text-xs font-normal text-zinc-500">
                                    {comparisonRunIds.length > 0
                                        ? `${comparisonRunIds.length} selected`
                                        : `${comparisonCandidates.length} completed runs in this study`}
                                </span>
                            }
                            title="Compare with other runs"
                            variant="card"
                        >
                            <p className="mb-3 text-xs text-zinc-500">
                                Select up to four other completed runs. The comparison flags every setting mismatch before placing pooled
                                cells side by side.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {comparisonCandidates.map((candidate) => (
                                    <label
                                        className="flex cursor-pointer items-center gap-2 rounded border border-zinc-800 bg-black/20 px-3 py-2 text-sm"
                                        key={candidate.id}
                                    >
                                        <input
                                            checked={comparisonRunIds.includes(candidate.id)}
                                            disabled={!comparisonRunIds.includes(candidate.id) && comparisonRunIds.length >= 4}
                                            type="checkbox"
                                            onChange={() => toggleComparisonRun(candidate.id)}
                                        />
                                        <span className="text-zinc-200">{candidate.label}</span>
                                        <span className="text-xs text-zinc-500">
                                            #{candidate.id} · {candidate.primaryDuration}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {comparisonQuery.isLoading && comparisonRunIds.length > 0 ? (
                                <div className="mt-4">
                                    <DataLoadingSpinner text="Comparing runs..." />
                                </div>
                            ) : comparisonQuery.error instanceof Error ? (
                                <Callout className="mt-4" variant="error">
                                    {comparisonQuery.error.message}
                                </Callout>
                            ) : comparisonQuery.data ? (
                                <div className="mt-4 space-y-3">
                                    {comparisonQuery.data.compatibility.warnings.length === 0 ? (
                                        <Callout variant="success">These runs use directly comparable frozen settings.</Callout>
                                    ) : (
                                        <Callout title="Interpret with care" variant="warning">
                                            <ul className="list-disc space-y-1 pl-5">
                                                {comparisonQuery.data.compatibility.warnings.map((warning) => (
                                                    <li key={warning}>{warning}</li>
                                                ))}
                                            </ul>
                                        </Callout>
                                    )}
                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[900px] text-sm">
                                            <thead>
                                                <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-wide text-zinc-500">
                                                    <th className="py-2 pr-3">Run</th>
                                                    <th className="py-2 pr-3">Variant</th>
                                                    <th className="py-2 pr-3">Lens</th>
                                                    <th className="py-2 pr-3">Horizon</th>
                                                    <th className="py-2 pr-3">Window</th>
                                                    <th className="py-2 pr-3 text-right">Support</th>
                                                    <th className="py-2 pr-3 text-right">Mean</th>
                                                    <th className="py-2 text-right">Random edge</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {comparisonQuery.data.runs.flatMap((comparisonRun) => {
                                                    const labels = new Map(
                                                        comparisonRun.variants.map((variant) => [variant.variantId, variant.label])
                                                    );

                                                    return comparisonRun.cells.map((cell) => (
                                                        <tr
                                                            className="border-b border-zinc-800/60"
                                                            key={`${comparisonRun.run.id}:${cell.variantId}:${cell.entityType}:${cell.lens}:${cell.horizonSeconds}:${cell.windowRole}`}
                                                        >
                                                            <td className="py-1.5 pr-3 text-zinc-200">{comparisonRun.run.label}</td>
                                                            <td className="py-1.5 pr-3 text-zinc-300">
                                                                {labels.get(cell.variantId) ?? `#${cell.variantId}`}
                                                            </td>
                                                            <td className="py-1.5 pr-3 text-zinc-400">
                                                                {marketGroupLabel(cell.entityType, cell.lens)}
                                                            </td>
                                                            <td className="py-1.5 pr-3 text-zinc-400">
                                                                {formatHorizonLabel(cell.horizonSeconds)}
                                                            </td>
                                                            <td className="py-1.5 pr-3 text-zinc-400">{cell.windowRole}</td>
                                                            <td className="py-1.5 pr-3 text-right text-zinc-300">{cell.supportCount}</td>
                                                            <td className="py-1.5 pr-3 text-right text-zinc-200">
                                                                {formatSignedPercent(cell.meanReturnPercent)}
                                                            </td>
                                                            <td className="py-1.5 text-right text-zinc-200">
                                                                {formatSignedPercent(cell.randomEdgeMeanPercent)}
                                                            </td>
                                                        </tr>
                                                    ));
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : null}
                        </Disclosure>
                    )}

                    <RunDiagnostics optionStrategyName={optionStrategyName} run={run} />
                </div>
            ) : null}

            {run && (
                <PromoteVariantModal
                    initialMarket={view?.entityType === 'All' ? null : (view?.entityType ?? null)}
                    initialLens={view?.lens ?? null}
                    isOpen={promoteTarget !== null}
                    run={run}
                    variant={promoteTarget}
                    onClose={() => setPromoteTarget(null)}
                />
            )}
            {study && run && (
                <CreateRunModal
                    isOpen={isRerunOpen}
                    sourceRunId={run.id}
                    study={study}
                    onClose={() => setIsRerunOpen(false)}
                    onCreated={(created) => {
                        setIsRerunOpen(false);
                        void navigate(`/user/ideaLab/run/${created.id}`);
                    }}
                />
            )}
            <ConfirmationDialog
                confirmText={cancelMutation.isPending ? 'Cancelling…' : 'Cancel run'}
                confirmVariant="danger"
                isOpen={confirmCancel}
                message="Cancel this run? Pending work is dropped; work already claimed by a worker finishes and is discarded."
                title="Cancel run"
                onCancel={() => setConfirmCancel(false)}
                onConfirm={() => run && !cancelMutation.isPending && cancelMutation.mutate({ runId: run.id })}
            />
        </UserLayout>
    );
};

export default IdeaLabRun;
